// eslint-disable-next-line no-use-before-define
import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import classNames from 'classnames';
import { UserContext } from 'op-contexts/index';

import ROPatientDiagnosisIntakeHNPage from './HNPage';
import ROPatientDiagnosisIntakeBreastPageRouter from './BreastPages/BreastPageRouter';
import ROPatientDiagnosisIntakeLungPage from './LungPage';
import ROPatientDiagnosisIntakeUrogenitalPage from './UrogenitalPage';
import ROPatientDiagnosisIntakeLymphomaPage from './LymphomaPage';
import ROPatientCarePlanPageFooter from '../../Footer';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { DiagnosisRouterParams } from '../Interface';
import { GET_DIAGNOSIS, LOG_CLINEX_MUTATION, SUBMIT_DIAGNOSIS_ONLY } from '../Queries';
import { DateValidationError } from '@mui/x-date-pickers';
import { useQuery, useMutation } from '@apollo/client';
import { CREATE_CAREPLAN, GET_CAREPLAN } from 'op-pages/RO/Careplan/Queries';
import { Logger, logPage } from 'shared-components/utils';
import ROPatientCarePlanContext from 'op-pages/RO/Careplan/ROPatientCarePlanContext';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import { LoadingSpinner, StandardDialog } from 'shared-components/components';
import { useWindowSize } from 'shared-components/utils/CustomHooks';
import CareplanContainer from '../../Container';
import { ClinexContext } from '../Clinex/context';
import {
  PLEASE_REVIEW_CLINEX,
  breastIntakeClinexMappings,
  urogenitalClinexMappings,
  lungIntakeClinexMappings,
  selectFirstHighlight,
} from '../Clinex/helpers';
import { GET_DIAGNOSIS_INTAKE, UPDATE_DIAGNOSIS_INTAKE } from './Queries';
import {
  CareplanBanners,
  changesFromLastEdited,
  ClinexBetaTestingBanner,
  hasSubmitted,
} from 'op-pages/RO/Careplan/Banner/Banner';
import { CareplanFormikValues } from '../../Interface';
import { CAREPLAN_STATUS, logMissingDiagnosisCode, ScrollToError } from '../../Common';
import { Typography } from '@mui/material';
import { IntakeData } from './Interface';

const logger = new Logger('ROPatientDiagnosisIntakePage');

export const intakePageMapping = {
  'H&N': {
    createVariable: { headAndNeck: { p16: '' } },
    key: 'headAndNeck',
    component: ROPatientDiagnosisIntakeHNPage,
  },
  Breast: {
    createVariable: { breast: { grade: '' } },
    key: 'breast',
    component: ROPatientDiagnosisIntakeBreastPageRouter,
    clinexMappings: breastIntakeClinexMappings,
  },
  Lung: {
    createVariable: { lung: { weightLoss: '' } },
    key: 'lung',
    component: ROPatientDiagnosisIntakeLungPage,
    clinexMappings: lungIntakeClinexMappings,
  },
  Lymphoma: {
    createVariable: { lymphoma: { stage: '' } },
    key: 'lymphoma',
    component: ROPatientDiagnosisIntakeLymphomaPage,
  },
  Urogenital: {
    createVariable: { urogenital: { isupGrade: '' } },
    key: 'urogenital',
    component: ROPatientDiagnosisIntakeUrogenitalPage,
    clinexMappings: urogenitalClinexMappings,
  },
};

const ROPatientDiagnosisIntakePage = (): JSX.Element => {
  const history = useHistory();
  const match = useRouteMatch<DiagnosisRouterParams>();
  // const [intake, setIntake] = useState<any>();
  const { state } = useContext(ROPatientCarePlanContext);
  const { diagnosisId, id: patientId, careplanId, oncologyType } = match.params;
  const [createCareplan] = useMutation(CREATE_CAREPLAN);
  const clinexContexts = useContext(ClinexContext);
  const { clinexData, uuid, setIntakeFieldsLoaded, setPageReady } = clinexContexts;
  const startTime = useRef<number>(new Date().getTime());
  const windowSize = useWindowSize();
  const isSmallDevice = windowSize.width < 1900;
  const [logEvent] = useMutation(LOG_CLINEX_MUTATION);
  const { data: careplan, loading: careplanLoading } = useQuery(GET_CAREPLAN, {
    fetchPolicy: 'network-only',
    variables: { id: careplanId },
  });
  const { values, setFieldValue, setFieldTouched, validateForm } = useFormikContext<CareplanFormikValues>();
  const { loading: intakeDataLoading } = useQuery(GET_DIAGNOSIS_INTAKE, {
    variables: { diagnosisId },
    fetchPolicy: 'cache-and-network',
  });
  const [updateIntake] = useMutation(UPDATE_DIAGNOSIS_INTAKE, {
    refetchQueries: [{ query: GET_CAREPLAN, variables: { id: careplanId } }],
    awaitRefetchQueries: true,
  });
  const { loading: dxLoading } = useQuery(GET_DIAGNOSIS, {
    variables: { diagnosisId },
    onCompleted: (data) => {
      logMissingDiagnosisCode(logger, data, 'Intake Information');
    },
  });
  const [submitDiagnosisOnly] = useMutation(SUBMIT_DIAGNOSIS_ONLY, {
    refetchQueries: [
      { query: GET_DIAGNOSIS, variables: { diagnosisId } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
    ],
    awaitRefetchQueries: true,
  });
  const { hasFeature } = useContext(UserContext);

  const intake = values?.intake;
  const intakePage = values?.intakePage as keyof typeof intakePageMapping;
  const [dateError, setDateError] = useState<DateValidationError | null>(null);
  const [hasValidationErrors, setHasValidationErrors] = useState<boolean>(false);
  const [submitDxModalOpen, setSubmitDxModalOpen] = useState<boolean>(false);

  const logClinex = () => {
    if (intakePage && intake && intakePageMapping[intakePage]?.key) {
      const pageKey = intakePageMapping[intakePage].key as keyof IntakeData;
      const intakeSection = intake[pageKey];
      if (intakeSection) {
        const intakeCopy = (({ __typename, id, ...o }) => o)(intakeSection as any);
        const pageData = {
          fields: intakeCopy,
          patientId,
          diagnosisId,
          page: 'intake',
          intakeType: intakePageMapping[intakePage].key,
          careplanId,
          clinexResponseUuid: uuid,
        };
        logPage(startTime?.current, pageData, logEvent);
      }
    }
  };

  useEffect(() => {
    if (!isSmallDevice && (values?.intake as any)?.id && (intakePageMapping[intakePage] as any)?.clinexMappings) {
      selectFirstHighlight((intakePageMapping[intakePage] as any).clinexMappings, clinexContexts);
    }
  }, [clinexData, intakePage, (values?.intake as any)?.id]);

  // Check for validation errors whenever form values change
  useEffect(() => {
    if (intakePage && values?.intake) {
      validateForm().then((errors: any) => {
        const hasErrors = Object.keys(errors).length > 0 || dateError !== null;
        setHasValidationErrors(hasErrors);
      });
    }
  }, [values, dateError, validateForm, intakePage]);

  // Mark intake fields as loaded when the current intake section exists
  const hasCurrentIntakeSection = Boolean(
    intakePage &&
      values?.intake &&
      intakePageMapping[intakePage]?.key &&
      values?.intake[intakePageMapping[intakePage]?.key as keyof IntakeData],
  );
  useEffect(() => {
    if (hasCurrentIntakeSection) {
      setIntakeFieldsLoaded(true);
    }
  }, [hasCurrentIntakeSection, setIntakeFieldsLoaded]);

  useEffect(() => {
    if (!intakePage || (dxLoading && !values?.diagnosis) || careplanLoading || intakeDataLoading) {
      setPageReady(false);
      return;
    }

    setPageReady(true);
  }, [intakePage, dxLoading, values?.diagnosis, careplanLoading, intakeDataLoading, setPageReady]);

  if (!intakePage || (dxLoading && !values?.diagnosis) || careplanLoading)
    return (
      <LoadingSpinner loadingText={'Loading Intake Info'} subtitle={'Please wait while we set things up for you'} />
    );

  const currentPageKey = intakePageMapping[intakePage]?.key;
  const currentIntakeCopy =
    values?.intake && currentPageKey && values?.intake[currentPageKey as keyof IntakeData]
      ? (({ __typename, id, ...o }) => o)(values.intake[currentPageKey as keyof IntakeData] as any)
      : {};

  let footerOptions = {};

  const isDxSubmittable =
    careplan?.careplan?.careplanStatus === CAREPLAN_STATUS.draft ||
    careplan?.careplan?.careplanStatus === CAREPLAN_STATUS.dxSubmitted;

  const hasSubmitDxOnlyFeatureEnabled = hasFeature('SubmitDxOnly');

  // Only show Submit Diagnosis & Exit button if careplan is in draft status
  if (isDxSubmittable && hasSubmitDxOnlyFeatureEnabled) {
    footerOptions = {
      additionLeftText: 'Submit Diagnosis & Exit',
      additionLeftDisabled:
        hasSubmitted(careplan?.careplan, CurrentAppConfig.DefaultTimezone) &&
        !changesFromLastEdited(careplan?.careplan),
      onAdditionLeft: (): void => {
        validateForm().then((errors: any) => {
          const intakePageFields = Object.keys(currentIntakeCopy);
          intakePageFields.forEach((field: string) => setFieldTouched(field));
          if (Object.keys(errors).length > 0 || dateError) return;
          setSubmitDxModalOpen(true);
        });
      },
    };
  }

  const handleSubmitDiagnosis = (): void => {
    setSubmitDxModalOpen(false);
    submitDiagnosisOnly({
      variables: { diagnosisId, careplanId },
    }).then(
      (): void => {
        history.push(`/${oncologyType}/patient/${patientId}/summary`);
      },
      (error): void => {
        logger.error(error);
      },
    );
  };

  const handleCloseSubmitModal = (): void => {
    setSubmitDxModalOpen(false);
  };

  if (hasCurrentIntakeSection) {
    const FormComponent = intakePageMapping[intakePage]?.component;
    const updateIntakeHandler = (changedValues: { [key: string]: string | string[] | number | null }): void => {
      const fieldKeys = Object.keys(changedValues);
      fieldKeys.forEach((field) =>
        setFieldValue(`intake.${[intakePageMapping[intakePage].key]}.${field}`, changedValues[field]),
      );

      updateIntake({
        variables: {
          diagnosisId,
          intakeData: {
            [`${currentPageKey}`]: {
              ...currentIntakeCopy,
              ...changedValues,
            },
          },
        },
      });
    };

    return (
      <div
        className={classNames('main-container-wrapper', {
          'clinex-wrapper': clinexData && !isSmallDevice,
        })}>
        <StandardDialog
          open={submitDxModalOpen}
          onClose={handleCloseSubmitModal}
          title="Submit Diagnosis and Exit"
          submitText="SUBMIT DIAGNOSIS"
          onSubmit={handleSubmitDiagnosis}>
          <Typography paddingY={1}>
            Submitting the diagnosis will add it to the patient record in the EMR. You may add a Careplan later. <br />
            You will be redirected back to the patient summary page after submission.
          </Typography>
        </StandardDialog>

        <CareplanBanners data={careplan} />
        {CurrentAppConfig.Clinex.betaTesting && clinexData && <ClinexBetaTestingBanner />}
        <CareplanContainer className={'ro-intake-page'}>
          <div className="page-header">
            <Typography variant="h5">Intake Info</Typography>
            {clinexData && PLEASE_REVIEW_CLINEX}
          </div>
          <FormComponent
            updateIntakeHandler={updateIntakeHandler}
            setDateError={setDateError}
            featureVersion={careplan?.careplan?.featureVersion}
          />
        </CareplanContainer>
        <ROPatientCarePlanPageFooter
          {...footerOptions}
          onBack={(): void => {
            history.push(
              `/${oncologyType}/patient/${patientId}/careplan/${careplanId}/diagnosis/${diagnosisId}/tnmstaging`,
            );
          }}
          onNext={(): void => {
            logClinex();
            validateForm().then((errors: any) => {
              const intakePageFields = Object.keys(currentIntakeCopy);
              intakePageFields.forEach((field: string) => setFieldTouched(field));
              if (Object.keys(errors).length > 0 || dateError) {
                ScrollToError();
              } else {
                history.push(`/${oncologyType}/patient/${patientId}/careplan/${state.careplan.id}/treatmentInfo`);
              }
            });
          }}
        />
      </div>
    );
  } else if (intakeDataLoading) {
    return (
      <LoadingSpinner
        loadingText={'Loading Intake Data'}
        subtitle={'Please wait while we fetch the intake information'}
      />
    );
  } else {
    return <Fragment />;
  }
};

export default ROPatientDiagnosisIntakePage;
